import { HelpCircleIcon, Share2Icon } from 'lucide-react';
import { TL, TL_PATIENTEN, TlKaart, TlKnop, TlWrap } from './chrome.js';

/**
 * Dashboard van het nagebouwde Therapieland-zorgverlenerportaal.
 * Nagebouwd naar docs/designs/screenshots-therapieland/dashboard-therapieland-ingelogd.png.
 *
 * In die screenshot zijn de patiëntgegevens weggelakt; die plekken zijn hier
 * gevuld met de fictieve demopatiënten uit TL_PATIENTEN.
 */

interface TlActiviteit {
  id: string;
  naam: string;
  moment: string;
  omschrijving: string;
}

// De eerste twee regels horen bij dezelfde patiënt, net als in de screenshot.
const ACTIVITEITEN: TlActiviteit[] = [
  {
    id: 'a1',
    naam: TL_PATIENTEN[0].naam,
    moment: `${TL_PATIENTEN[0].laatsteInlog} om 11:18`,
    omschrijving: 'Opkomen voor jezelf - Hoe zit het bij jou? - Ingevuld',
  },
  {
    id: 'a2',
    naam: TL_PATIENTEN[0].naam,
    moment: `${TL_PATIENTEN[0].laatsteInlog} om 10:39`,
    omschrijving: 'Opkomen voor jezelf - In behandeling',
  },
  {
    id: 'a3',
    naam: TL_PATIENTEN[1].naam,
    moment: `${TL_PATIENTEN[1].laatsteInlog} om 21:28`,
    omschrijving: 'Nieuwe activiteit',
  },
  {
    id: 'a4',
    naam: TL_PATIENTEN[2].naam,
    moment: `${TL_PATIENTEN[2].laatsteInlog} om 20:47`,
    omschrijving: 'Omgaan met stress - In behandeling',
  },
  {
    id: 'a5',
    naam: TL_PATIENTEN[3].naam,
    moment: `${TL_PATIENTEN[3].laatsteInlog} om 19:12`,
    omschrijving: 'Nieuwe activiteit',
  },
  {
    id: 'a6',
    naam: TL_PATIENTEN[4].naam,
    moment: `${TL_PATIENTEN[4].laatsteInlog} om 21:01`,
    omschrijving: 'Nieuwe activiteit',
  },
];

interface TlSegment {
  label: string;
  waarde: number;
  kleur: string;
  /** Het groene segment is te smal voor een getal in de ring. */
  toonLabel: boolean;
}

const SEGMENTEN: TlSegment[] = [
  { label: 'Recent ingelogd', waarde: 5, kleur: TL.groen, toonLabel: false },
  { label: 'Niet recent ingelogd', waarde: 204, kleur: TL.oranje, toonLabel: true },
  { label: 'Openstaande uitnodigingen', waarde: 22, kleur: TL.rood, toonLabel: true },
];

export function TlDashboard({ onPatienten }: { onPatienten: () => void }): JSX.Element {
  return (
    <TlWrap>
      <div className="grid grid-cols-[1fr_300px] items-start gap-7">
        <TlKaart>
          <div className="px-6 py-4" style={{ borderBottom: `1px solid ${TL.rand}` }}>
            <h2 className="text-[17px] font-bold" style={{ color: TL.ink }}>
              Activiteiten overzicht
            </h2>
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">
            {ACTIVITEITEN.map((a) => (
              <TlActiviteitRegel key={a.id} activiteit={a} />
            ))}
          </div>

          <div className="flex justify-center pb-7">
            <TlKnop variant="omlijnd">Laad meer</TlKnop>
          </div>
        </TlKaart>

        <div className="flex flex-col gap-5">
          <div className="[&>button]:w-full">
            <TlKnop onClick={onPatienten}>+ Patiënt toevoegen</TlKnop>
          </div>

          <TlKaart>
            <div className="px-6 py-4" style={{ borderBottom: `1px solid ${TL.rand}` }}>
              <h2 className="text-[17px] font-bold" style={{ color: TL.ink }}>
                Patiënten
              </h2>
            </div>

            <div className="px-6 pb-6 pt-6">
              <TlDonut />
              <div className="mt-7 flex flex-col gap-2.5">
                {SEGMENTEN.map((s) => (
                  <span key={s.label} className="flex items-center gap-2.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: s.kleur }}
                    />
                    <span className="text-[14px]" style={{ color: TL.body }}>
                      {s.label}
                    </span>
                    <HelpCircleIcon size={14} style={{ color: TL.zacht }} />
                  </span>
                ))}
              </div>
            </div>
          </TlKaart>
        </div>
      </div>
    </TlWrap>
  );
}

function TlActiviteitRegel({ activiteit }: { activiteit: TlActiviteit }): JSX.Element {
  return (
    <div className="flex items-start gap-3.5">
      <span
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: TL.primair }}
      >
        <Share2Icon size={14} strokeWidth={2} className="text-white" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <span className="text-[15px] font-bold" style={{ color: TL.ink }}>
            {activiteit.naam}
          </span>
          <span className="text-[13px]" style={{ color: TL.muted }}>
            {activiteit.moment}
          </span>
        </div>
        <p className="mt-1 text-[14.5px]" style={{ color: TL.body }}>
          {activiteit.omschrijving}
        </p>
      </div>

      <span
        className="mt-2.5 h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: TL.primair }}
      />
    </div>
  );
}

const DONUT_R = 85;
const DONUT_OMTREK = 2 * Math.PI * DONUT_R;
/** Booglengte die wit blijft tussen twee segmenten, zoals in het origineel. */
const DONUT_GAT = 4;

function TlDonut(): JSX.Element {
  const totaal = SEGMENTEN.reduce((som, s) => som + s.waarde, 0);

  let gelopen = 0;
  const bogen = SEGMENTEN.map((s) => {
    const lengte = (s.waarde / totaal) * DONUT_OMTREK;
    const midden = ((gelopen + lengte / 2) / DONUT_OMTREK) * 2 * Math.PI;
    const boog = {
      segment: s,
      lengte,
      offset: gelopen,
      labelX: 120 + DONUT_R * Math.sin(midden),
      labelY: 120 - DONUT_R * Math.cos(midden),
    };
    gelopen += lengte;
    return boog;
  });

  return (
    <svg viewBox="0 0 240 240" className="mx-auto block w-[230px]" role="img">
      <title>Verdeling van patiënten naar inlogstatus</title>
      <g transform="rotate(-90 120 120)">
        {bogen.map((b) => (
          <circle
            key={b.segment.label}
            cx={120}
            cy={120}
            r={DONUT_R}
            fill="none"
            stroke={b.segment.kleur}
            strokeWidth={34}
            strokeDasharray={`${b.lengte - DONUT_GAT} ${DONUT_OMTREK - b.lengte + DONUT_GAT}`}
            strokeDashoffset={-b.offset}
          />
        ))}
      </g>
      {bogen
        .filter((b) => b.segment.toonLabel)
        .map((b) => (
          <text
            key={b.segment.label}
            x={b.labelX}
            y={b.labelY}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={16}
            fill={TL.ink}
          >
            {b.segment.waarde}
          </text>
        ))}
    </svg>
  );
}
